import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Box } from 'foundations-components/transitional/components/Box';
import { View } from 'foundations-components/components/View';
import _capitalize from 'hs-lodash/capitalize';
import _get from 'hs-lodash/get';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ACTIONS, SCREENS } from '../../../constants/Tracking';
import { LINKED_PROPERTIES_CONFIG } from '../../../constants/associations';
import { propertyFieldTypes } from '../../../constants/properties';
import { objectTypes, pluralTypeToSingle, singularTypeToPlural } from '../../../constants/types';
import { AppContext } from '../../../contexts/app/context';
import { PreviewModalPropsContext } from '../../../contexts/previewModal/PropsContext';
import { PreviewModalContext } from '../../../contexts/previewModal/context';
import { PropertiesContext } from '../../../contexts/properties/context';
import editPropertyValidators from '../../../validators/editPropertyValidators';
import FormInputs from '../../common/FormInputs/FormInputs';
import EditControls from '../EditControls/EditControls';
import { FormControlWrapper } from './EditPropertyWrapper.style';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function EditPropertyWrapper({
  children,
  type,
  propertyName,
  currentValue,
  objectId,
  openLinkedPropsModal
}) {
  const {
    usageTracker,
    portalId,
    openUrl,
    apiWrapper,
    crmObjectType,
    updateCrmObjectCallback,
    prosemirrorTrackerProperties,
    hubspotUserId
  } = useContext(PreviewModalPropsContext);
  const {
    isPastMeeting,
    appName
  } = useContext(AppContext);
  const {
    previewModalActions
  } = useContext(PreviewModalContext);
  const {
    isValidating,
    validatingField,
    validationErrors,
    searchProperties
  } = useContext(PropertiesContext);
  const [isValid, setIsValid] = useState(false);
  const [isHover, setIsHover] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [canSave, setCanSave] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [currentProperty, setCurrentProperty] = useState(undefined);
  const [editValue, setEditValue] = useState({
    key: propertyName,
    value: currentValue
  });
  const propertyValidationError = useMemo(() => _get(validationErrors[propertyName], 'message', ''), [propertyName, validationErrors]);
  const onMouseEnterHandler = () => setIsHover(true);
  const onMouseLeaveHandler = () => setIsHover(false);
  const startEdit = useCallback(() => {
    usageTracker.trackInteraction(Object.assign({
      screen: SCREENS[`associated${_capitalize(pluralTypeToSingle[type])}`],
      type: pluralTypeToSingle[type]
    }, ACTIONS.general.clickStartEdit));
    if (LINKED_PROPERTIES_CONFIG[type][propertyName]) {
      openLinkedPropsModal();
    } else {
      setIsEdit(true);
    }
  }, [type, usageTracker, propertyName, openLinkedPropsModal]);
  const cancelEdit = useCallback(() => {
    setErrorMessage('');
    setIsEdit(false);
    setIsHover(false);
    usageTracker.trackInteraction(Object.assign({
      screen: SCREENS[`associated${_capitalize(pluralTypeToSingle[type])}`],
      type: pluralTypeToSingle[type]
    }, ACTIONS.general.clickCancelEdit));
  }, [type, usageTracker]);
  const disableLoading = (error = '') => {
    if (error) setErrorMessage(error);else {
      setIsEdit(false);
      setIsHover(false);
    }
    setIsLoading(false);
  };
  const onValueChangeHandler = ({
    key,
    value
  }) => {
    setEditValue({
      key,
      value
    });
  };
  const saveHandler = useCallback(() => {
    const {
      key,
      value
    } = editValue;
    if (value !== currentValue && currentProperty && isValid) {
      setIsLoading(true);
      previewModalActions.updateAssociatedObject({
        objectId,
        objectType: pluralTypeToSingle[type],
        data: {
          [key]: value
        },
        cb: disableLoading,
        isDate: currentProperty.fieldType === propertyFieldTypes.date,
        usageTracker,
        updateCb: updateCrmObjectCallback
      });
    }
  }, [currentValue, editValue, objectId, type, currentProperty, isValid, usageTracker, updateCrmObjectCallback, previewModalActions]);

  // check id we have property for this field => we can use it
  useEffect(() => {
    if (searchProperties[pluralTypeToSingle[type]].length) {
      setCurrentProperty(searchProperties[pluralTypeToSingle[type]].find(property => property.name === propertyName));
    }
  }, [searchProperties, type, propertyName]);

  // listen for field change => allow update only if it chanhes
  useEffect(() => {
    const currentPropertyValidating = isValidating && validatingField === propertyName;
    const valid = editPropertyValidators(propertyName, editValue.value);
    setIsValid(valid && !propertyValidationError);
    setCanSave(currentValue !== editValue.value && valid && !currentPropertyValidating && !propertyValidationError);
  }, [currentValue, editValue, propertyName, isValidating, propertyValidationError, validatingField]);
  if (isPastMeeting || type === objectTypes.meetings) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: children
    });
  }
  if (isEdit && currentProperty) {
    return /*#__PURE__*/_jsx(View, {
      position: "relative",
      inlineSize: "fit-content",
      paddingInlineEnd: "700",
      children: /*#__PURE__*/_jsxs(Box, {
        "data-test-id": `${appName}-editable-wrapper`,
        marginBlockEnd: "300",
        children: [/*#__PURE__*/_jsx(FormControlWrapper, {
          validationError: !isValid,
          children: /*#__PURE__*/_jsx(FormControl, {
            required: currentProperty.required,
            label: currentProperty.label,
            error: !!errorMessage || !!propertyValidationError,
            validationMessage: errorMessage || propertyValidationError,
            children: /*#__PURE__*/_jsx(FormInputs, {
              property: currentProperty,
              onFormValueChanged: onValueChangeHandler,
              currentValue: editValue.value,
              editMode: isEdit,
              objectType: singularTypeToPlural[crmObjectType] || objectTypes.contacts,
              portalId: portalId,
              openUrl: openUrl,
              snippetsApiWrapper: apiWrapper.snippets,
              prosemirrorTrackerProperties: prosemirrorTrackerProperties,
              autoFocus: true,
              hubspotUserId: hubspotUserId
            })
          })
        }), /*#__PURE__*/_jsx(EditControls, {
          currentProperty: currentProperty,
          isEdit: isEdit,
          isLoading: isLoading,
          errorMessage: errorMessage,
          propertyValidationError: propertyValidationError,
          cancelEdit: cancelEdit,
          canSave: canSave,
          startEdit: startEdit,
          saveHandler: saveHandler,
          isHover: isHover
        })]
      })
    });
  }
  return /*#__PURE__*/_jsx(View, {
    position: "relative",
    inlineSize: "fit-content",
    paddingInlineEnd: "700",
    children: /*#__PURE__*/_jsxs(Box, {
      onMouseEnter: onMouseEnterHandler,
      onMouseLeave: onMouseLeaveHandler,
      "data-test-id": `${appName}-editable-wrapper`,
      children: [children, /*#__PURE__*/_jsx(EditControls, {
        currentProperty: currentProperty,
        isEdit: isEdit,
        isLoading: isLoading,
        errorMessage: errorMessage,
        propertyValidationError: propertyValidationError,
        cancelEdit: cancelEdit,
        canSave: canSave,
        startEdit: startEdit,
        saveHandler: saveHandler,
        isHover: isHover
      })]
    })
  });
}
export default EditPropertyWrapper;